import { useState, useEffect, useCallback, useMemo } from 'react'
import { usePublicClient } from 'wagmi'
import { parseAbi, formatEther, keccak256, toBytes } from 'viem'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { getAdsMeta } from '../../../services/ads'

const MARKET_ADDRESS = import.meta.env.VITE_AD_MARKETPLACE_ADDRESS
const SEPOLIA_ID     = 11155111

const MARKET_ABI = parseAbi([
  'struct Ad { address advertiser; bytes32 placementId; uint256 durationDays; string metadataURI; uint8 payToken; uint256 amountPaid; uint8 status; bool refunded; bool withdrawn; }',
  'function getAd(uint256 adId) external view returns (Ad)',
  'function nextAdId() external view returns (uint256)',
])

const PLACEMENTS = [
  { name: 'feed',   label: 'Main Feed' },
  { name: 'banner', label: 'Marketplace Banner' },
]

const STATUS_LABEL = ['Pending', 'Approved', 'Rejected']
const STATUS_COLOR = [
  'text-yellow-500 bg-yellow-500/10 border-yellow-500/30',
  'text-green-500 bg-green-500/10 border-green-500/30',
  'text-error bg-error/10 border-error/30',
]
const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 0, label: 'Pending' },
  { key: 1, label: 'Approved' },
  { key: 2, label: 'Rejected' },
]

function placementId(name) {
  return keccak256(toBytes(name))
}

function placementLabel(id) {
  return PLACEMENTS.find(p => placementId(p.name) === id)?.label ?? 'Unknown'
}

function parseMeta(metadataURI) {
  try { return JSON.parse(atob(metadataURI?.split(',')[1] ?? '')) } catch { return {} }
}

function fmtSpend(ad) {
  if (ad.amountPaid == null) return '—'
  const n = Number(formatEther(ad.amountPaid))
  const symbol = ad.payToken === 0 ? 'ETH' : 'CLT'
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${symbol}`
}

// Rough USD-style comparison isn't possible across ETH/CLT without a price
// feed, so spend totals are kept separate per token rather than summed.
function csvEscape(value) {
  const s = String(value ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export function CampaignAnalytics() {
  const publicClient = usePublicClient({ chainId: SEPOLIA_ID })

  const [ads, setAds] = useState([])
  const [adsMeta, setAdsMeta] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [statusFilter, setStatusFilter] = useState('all')
  const [placementFilter, setPlacementFilter] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const load = useCallback(async () => {
    if (!MARKET_ADDRESS || !publicClient) return
    setLoading(true)
    setError(null)
    try {
      const [metaById, nextAdId] = await Promise.all([
        getAdsMeta().catch(() => ({})), // views/createdAt are nice-to-have; don't block the chain read if this fails
        publicClient.readContract({ address: MARKET_ADDRESS, abi: MARKET_ABI, functionName: 'nextAdId' }),
      ])
      setAdsMeta(metaById)

      const total = Number(nextAdId)
      const results = []
      for (let i = 0; i < total; i++) {
        try {
          const ad = await publicClient.readContract({
            address: MARKET_ADDRESS,
            abi: MARKET_ABI,
            functionName: 'getAd',
            args: [BigInt(i)],
          })
          results.push({ id: i, ...ad })
        } catch {}
      }
      setAds(results.reverse())
    } catch (err) {
      setError(err.shortMessage ?? err.message ?? 'Failed to load campaign data')
    } finally {
      setLoading(false)
    }
  }, [publicClient])

  useEffect(() => { load() }, [load])

  const rows = useMemo(() => {
    return ads
      .map(ad => {
        const meta = parseMeta(ad.metadataURI)
        const backendMeta = adsMeta[ad.id] ?? {}
        return {
          id: ad.id,
          title: meta.title || `Ad #${ad.id}`,
          placement: placementLabel(ad.placementId),
          status: ad.status,
          spend: fmtSpend(ad),
          payToken: ad.payToken,
          amountPaidWei: ad.amountPaid,
          views: backendMeta.views ?? 0,
          createdAt: backendMeta.createdAt,
          advertiser: ad.advertiser,
        }
      })
      .filter(row => statusFilter === 'all' || row.status === statusFilter)
      .filter(row => placementFilter === 'all' || row.placement === placementFilter)
      .filter(row => {
        if (!row.createdAt) return !startDate && !endDate // no date on record — only show when no filter is set
        const t = new Date(row.createdAt).getTime()
        if (startDate && t < new Date(startDate).getTime()) return false
        if (endDate && t > new Date(endDate).getTime() + 86400000 - 1) return false
        return true
      })
  }, [ads, adsMeta, statusFilter, placementFilter, startDate, endDate])

  const totals = useMemo(() => {
    const totalViews = rows.reduce((sum, r) => sum + (r.views ?? 0), 0)
    const ethSpend = rows.filter(r => r.payToken === 0).reduce((sum, r) => sum + Number(formatEther(r.amountPaidWei ?? 0n)), 0)
    const cltSpend = rows.filter(r => r.payToken === 1).reduce((sum, r) => sum + Number(formatEther(r.amountPaidWei ?? 0n)), 0)
    return { totalViews, ethSpend, cltSpend, count: rows.length }
  }, [rows])

  const exportCsv = () => {
    const header = ['Campaign', 'Placement', 'Status', 'Spend', 'Views', 'Created', 'Advertiser']
    const lines = [header.join(',')]
    for (const r of rows) {
      lines.push([
        csvEscape(r.title),
        csvEscape(r.placement),
        csvEscape(STATUS_LABEL[r.status]),
        csvEscape(r.spend),
        csvEscape(r.views),
        csvEscape(r.createdAt ? new Date(r.createdAt).toISOString().slice(0, 10) : ''),
        csvEscape(r.advertiser),
      ].join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `campaign-performance-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (!MARKET_ADDRESS) {
    return <AdminAlert type="error" message="Ad marketplace contract address is not configured (VITE_AD_MARKETPLACE_ADDRESS)." />
  }

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="font-extrabold text-[18px] uppercase tracking-widest mb-1">Campaign Performance</h2>
        <p className="text-[12px] text-on-surface-variant">
          Status and spend come from the ad marketplace contract; views come from the platform's record of each campaign.
          Clicks and conversions aren't tracked anywhere yet, so they aren't shown here.
        </p>
      </div>

      {error && <AdminAlert type="error" message={error} />}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : (
          <>
            <div className="border-4 border-on-background bg-surface-container-low p-5 flex flex-col gap-1" style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
              <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">Campaigns</p>
              <p className="font-extrabold text-[26px] leading-none">{totals.count}</p>
            </div>
            <div className="border-4 border-on-background bg-surface-container-low p-5 flex flex-col gap-1" style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
              <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">Total Views</p>
              <p className="font-extrabold text-[26px] leading-none">{totals.totalViews.toLocaleString()}</p>
            </div>
            <div className="border-4 border-on-background bg-surface-container-low p-5 flex flex-col gap-1" style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
              <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">ETH Spend</p>
              <p className="font-extrabold text-[22px] leading-none">{totals.ethSpend.toFixed(4)}</p>
            </div>
            <div className="border-4 border-on-background bg-surface-container-low p-5 flex flex-col gap-1" style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
              <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">CLT Spend</p>
              <p className="font-extrabold text-[22px] leading-none">{totals.cltSpend.toFixed(2)}</p>
            </div>
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <span className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Status</span>
          <div className="flex gap-1 border-2 border-on-background w-fit">
            {STATUS_FILTERS.map(f => (
              <button key={f.label} onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 font-bold text-[11px] uppercase tracking-widest transition-colors ${
                  statusFilter === f.key ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Placement</span>
          <select value={placementFilter} onChange={e => setPlacementFilter(e.target.value)}
            className="bg-surface border-2 border-on-background px-3 py-1.5 text-[12px] font-bold uppercase tracking-wider focus:outline-none focus:border-primary-container">
            <option value="all">All</option>
            {PLACEMENTS.map(p => <option key={p.name} value={p.label}>{p.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">From</span>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="bg-surface border-2 border-on-background px-3 py-1.5 text-[12px] focus:outline-none focus:border-primary-container" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">To</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="bg-surface border-2 border-on-background px-3 py-1.5 text-[12px] focus:outline-none focus:border-primary-container" />
        </div>
        <button onClick={exportCsv} disabled={loading || rows.length === 0}
          className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors disabled:opacity-40 ml-auto">
          Export CSV
        </button>
        <button onClick={load} disabled={loading}
          className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-8 text-center">
          <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No campaigns match these filters</p>
        </div>
      ) : (
        <div className="overflow-x-auto border-4 border-on-background" style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-surface-container-low border-b-4 border-on-background text-left">
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-[10px]">Campaign</th>
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-[10px]">Placement</th>
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-[10px]">Status</th>
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-[10px]">Spend</th>
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-[10px]">Views</th>
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-[10px]">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b-2 border-on-background/10 last:border-b-0 hover:bg-surface-container-low/50">
                  <td className="px-4 py-3 font-bold truncate max-w-[200px]">{r.title}</td>
                  <td className="px-4 py-3 text-on-surface-variant">{r.placement}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 border font-bold text-[10px] uppercase tracking-widest ${STATUS_COLOR[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono">{r.spend}</td>
                  <td className="px-4 py-3 font-mono">{r.views.toLocaleString()}</td>
                  <td className="px-4 py-3 text-on-surface-variant font-mono">
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}